import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import {
    IncidentsTypesLaboratoriesGridState,
    initialIncidentsTypesLaboratoriesGridState
} from '@/modules/configurationModule/detail/incidents/incidents-types/components/incidents-types-grid/incidents-types-grid.model'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import {
    ConfigurationIncidentsTypesLaboratoriesCreateAndPatchRequest,
    ConfigurationIncidentsTypesLaboratoriesResponse
} from '@/modules/configurationModule/detail/incidents/incidents-types/services/incidents-types-laboratories.dto'
import IncidentsTypesModal
    from '@/modules/configurationModule/detail/incidents/incidents-types/components/incidents-types-modal/incidents-types-modal.vue'
import { incidentsTypesLaboratoriesConfigurationServices } from '@/modules/configurationModule/detail/incidents/incidents-types/services/incidents-types-laboratories.service'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        IncidentsTypesModal
    }
})
export default class IncidentsTypesGrid extends BaseCrudServerSideComponent<ConfigurationIncidentsTypesLaboratoriesResponse> implements BaseCrud {
    @Ref() readonly incidentsTypesLaboratoriesModalWrapper!: any

    state: IncidentsTypesLaboratoriesGridState
    constructor() {
        super()
        this.state = initialIncidentsTypesLaboratoriesGridState()
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        const numberRowSelected: number = this.state.selectedRows.length
        return numberRowSelected === 1
    }

    private _showModalUpdateAction(): void {
        this.showModal(this.$t('configuration.actions.update').toString(), this.state.selectedRows[0], this.incidentsTypesLaboratoriesModalWrapper)
    }

    private _showModalCreateAction(): void {
        this.showModal(this.$t('configuration.actions.add').toString(), null, this.incidentsTypesLaboratoriesModalWrapper)
    }

    private _onPressShortKeyUpdateButton(): void {
        this._showModalUpdateAction()
    }

    private _onClickUpdateButton(): void {
        this._showModalUpdateAction()
    }

    private _onClickCreateButton(): void {
        this._showModalCreateAction()
    }

    private _onPressShortKeyCreateButton(): void {
        this._showModalCreateAction()
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params.node
                    const selectedRow: ConfigurationIncidentsTypesLaboratoriesResponse = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update') as string, selectedRow, this.incidentsTypesLaboratoriesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: ConfigurationIncidentsTypesLaboratoriesResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: ConfigurationIncidentsTypesLaboratoriesResponse) => row.active !== option)
        const updatedFilteredRowActiveState: ConfigurationIncidentsTypesLaboratoriesCreateAndPatchRequest[] = filteredRowActiveState?.map((row: ConfigurationIncidentsTypesLaboratoriesResponse) => {
            return {
                id: row.id,
                active: option
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(incidentsTypesLaboratoriesConfigurationServices.patchIncidentsTypesLaboratory(this.state.labId, updatedFilteredRowActiveState), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async createOperation(row: ConfigurationIncidentsTypesLaboratoriesCreateAndPatchRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsTypesLaboratoriesConfigurationServices.createIncidentsTypesLaboratory(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.incidentsTypesLaboratoriesModalWrapper.hide()
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(rows: ConfigurationIncidentsTypesLaboratoriesCreateAndPatchRequest[]): Promise<void> {
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsTypesLaboratoriesConfigurationServices.patchIncidentsTypesLaboratory(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.incidentsTypesLaboratoriesModalWrapper.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteOperation(rows: ConfigurationIncidentsTypesLaboratoriesResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }

    private async _deleteBatchOperation(rows: ConfigurationIncidentsTypesLaboratoriesResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((incidentType: ConfigurationIncidentsTypesLaboratoriesResponse) => Number(incidentType?.id))
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsTypesLaboratoriesConfigurationServices.deleteIncidentsTypesLaboratory(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
